import React, { useState } from "react";
import {
  Box,
  Modal,
  Typography,
  Button,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
} from "@mui/material";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import Loader from "components/Loader";
import ErrorToast from "components/Toaster/ErrorToast";
import SuccessToast from "components/Toaster/SuccesToast";
import api from '../api';
import "./meeting-modal.css";

const MeetingSchedulerModal = ({
  open,
  onClose,
  selectedDate,
  handleDateChange,
  timeSlots,
  selectedTime,
  handleTimeChange,
  menteeData,
  courseId,
  title = "Schedule your call",
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    if (isNaN(date)) return "Invalid Date";
    return date.toISOString().split("T")[0];
  };

  const handleScheduleMeeting = async () => {
    if (!selectedTime) {
      ErrorToast({ message: "Please select a time slot" });
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    setShowError(false);

    try {
      const formattedDate = dayjs(selectedDate).format("DD-MM-YYYY");
      const endpoint = `course/schedule-adhoc-meeting/${courseId}/`;   // module classes are scheduled from Session Tracking

      const body = {
        "selected-date": formattedDate,
        "selected-time": selectedTime,
      };

      const response = await api.post(endpoint, body);
      setResponseData(response.data);
      setShowSuccessToast(true);
    } catch (err) {
      const message = err.response?.data?.message || err.response?.data?.error || "Failed to schedule meeting";
      setErrorMessage(message);
      setShowError(true);
      ErrorToast({ message });
    } finally {
      setIsLoading(false);
    }
  };

  const renderSuccessView = () => (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      height="100%"
    >
      <Typography variant="h5" gutterBottom>
        {responseData.message || "Your meeting is scheduled!"}
      </Typography>
      {responseData.meeting_link ? (
        <Typography variant="body1" align="center" gutterBottom>
          Meeting Link: <Link href={responseData.meeting_link} target="_blank" rel="noopener noreferrer">
            {responseData.meeting_link}
          </Link>
        </Typography>
      ) : (
        <Typography variant="body1" align="center">
          Please accept calendar invite in your email.
        </Typography>
      )}
      <Button
        variant="contained"
        color="primary"
        onClick={onClose}
        sx={{ mt: 2 }}
      >
        Close
      </Button>
    </Box>
  );

  const renderTimeSlots = () => (
    <Box mt={2}>
      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          fontFamily: "Be Vietnam Pro",
          color: "#6b7280",
        }}
      >
        <AccessTimeOutlinedIcon sx={{ marginRight: 1 }} />
        Start Time
      </Typography>
      <TableContainer
        component={Paper}
        style={{
          maxHeight: 400,
          overflowY: "auto",
          boxShadow: "none",
        }}
      >
        <Table>
          <TableBody>
            {timeSlots?.map((time, index) => (
              <TableRow
                key={index}
                onClick={() => handleTimeChange(time)}
                sx={{ cursor: "pointer" }}
              >
                <TableCell
                  sx={{
                    backgroundColor: selectedTime === time ? "#0172A6" : "inherit",
                    color: selectedTime === time ? "white" : "#6b7280",
                    padding: "8px",
                    fontFamily: "Be Vietnam Pro",
                  }}
                >
                  {time}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          width: 700,
          height: 600,
          borderRadius: "12px",
          overflow: "auto",
        }}
      >
        {isLoading ? (
          <Loader />
        ) : responseData ? (
          renderSuccessView()
        ) : (
          <>
            <Box display={"flex"} width={"100%"}>
              <Box
                display={"flex"}
                width={"100%"}
                justifyContent={"space-between"}
              >
                <Box display={"flex"}>
                  <Typography
                    width={"118px"}
                    fontSize={"16px"}
                    fontWeight={600}
                    fontFamily="Be Vietnam Pro"
                  >
                    {title}
                  </Typography>
                  {menteeData && (
                    <Box display={"flex"} flexDirection={"column"} ml={1}>
                      <Typography sx={{ color: "#0172A6", fontSize: "16px", fontFamily: "Be Vietnam Pro" }}>
                        {menteeData?.name || "N/A"}
                      </Typography>
                      <Typography sx={{ color: "#6b7280", fontSize: "14px", fontFamily: "Be Vietnam Pro" }}>
                        {menteeData?.state || "N/A"}, {menteeData?.country || "N/A"}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#6b7280",
                      whiteSpace: "nowrap",
                      fontFamily: "Be Vietnam Pro",
                    }}
                  >
                    Course Started {formatDate(menteeData?.timestamp)}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      color: "#202124",
                      whiteSpace: "nowrap",
                      fontFamily: "Be Vietnam Pro",
                    }}
                  >
                    {menteeData?.name}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box
              mt={2}
              display={"flex"}
              justifyContent={"space-evenly"}
              height={"450px"}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <StaticDatePicker
                  label="Pick a date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  disablePast
                  sx={{
                    '& .MuiPickersDay-root.Mui-selected': {
                      backgroundColor: '#0172A6',
                      color: '#fff',
                    },
                  }}
                />
              </LocalizationProvider>
              {renderTimeSlots()}
            </Box>
            <Box display={"flex"} justifyContent={"end"} mt={1}>
              <Button
                sx={{
                  color: "#202124",
                  fontSize: "14px",
                  minHeight: "auto",
                  fontFamily: "Be Vietnam Pro",
                  textTransform: "uppercase",
                }}
                size="small"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                sx={{
                  backgroundColor: "#0172A6",
                  fontSize: "14px",
                  borderRadius: "6px",
                  color: "white",
                  ml: 2,
                  minHeight: "auto",
                  fontFamily: "Be Vietnam Pro",
                  textTransform: "uppercase",
                  "&:hover": {
                    backgroundColor: "#0172A6",
                  },
                }}
                size="small"
                onClick={handleScheduleMeeting}
              >
                Save
              </Button>
            </Box>
            {showError && <ErrorToast message={errorMessage} />}
            {showSuccessToast && <SuccessToast message='Successfully scheduled the meeting.' />}
          </>
        )}
      </Box>
    </Modal>
  );
};

export default MeetingSchedulerModal;
